<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { subDays, differenceInCalendarDays } from 'date-fns';
import {
  getUnixStartOfUtcDay,
  getUnixEndOfUtcDay,
  unixToUtcCalendarDate,
  utcCalendarToday,
} from '../../helpers/utcDateHelper';
import {
  buildFilterList,
  buildLabelsList,
  buildRatingsList,
  getFilterType,
  RESOLUTION_METHOD_OPTIONS,
  HAS_COMMENT_OPTIONS,
} from './CsatFilterHelpers';
import FilterButton from 'dashboard/components/ui/Dropdown/DropdownButton.vue';
import AddFilterChip from '../Filters/v3/AddFilterChip.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
  MAX_COMPARISON_PERIODS,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  showTeamFilter: {
    type: Boolean,
    default: false,
  },
  displayType: { type: String, default: '' },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);

const initialDateRange =
  urlParams.from && urlParams.to
    ? [
        unixToUtcCalendarDate(urlParams.from),
        unixToUtcCalendarDate(urlParams.to),
      ]
    : [subDays(utcCalendarToday(), 6), utcCalendarToday()];

const showDropdownMenu = ref(false);
const customDateRange = ref(initialDateRange);
const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS);
const comparisonRanges = ref(
  (urlParams.comparisonPeriods || []).map(period => ({
    dates: [
      unixToUtcCalendarDate(period.from),
      unixToUtcCalendarDate(period.to),
    ],
    rangeType: DATE_RANGE_TYPES.CUSTOM_RANGE,
  }))
);

const appliedFilters = ref({
  user_ids: urlFilters.agent_id ? [urlFilters.agent_id] : [],
  inbox_id: urlFilters.inbox_id ? [urlFilters.inbox_id] : [],
  team_id: urlFilters.team_id ? [urlFilters.team_id] : [],
  rating: urlFilters.rating ? [urlFilters.rating] : [],
  label_title: urlFilters.label_title ? [urlFilters.label_title].flat() : [],
  exclude_label_title: urlFilters.exclude_label_title
    ? [urlFilters.exclude_label_title].flat()
    : [],
  resolution_method: urlFilters.resolution_method
    ? [urlFilters.resolution_method]
    : [],
  has_comment: urlFilters.has_comment ? [urlFilters.has_comment] : [],
});

const labelChipOrder = ref([
  ...appliedFilters.value.label_title,
  ...appliedFilters.value.exclude_label_title.filter(
    id => !appliedFilters.value.label_title.includes(id)
  ),
]);

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);
const labels = computed(() => store.getters['labels/getLabels']);

const ratings = computed(() => buildRatingsList(t, props.displayType));

const from = computed(() => getUnixStartOfUtcDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfUtcDay(customDateRange.value[1]));

const comparisonPeriodsPayload = () =>
  comparisonRanges.value.map(cr => ({
    from: getUnixStartOfUtcDay(cr.dates[0]),
    to: getUnixEndOfUtcDay(cr.dates[1]),
  }));

const resolutionMethods = computed(() => RESOLUTION_METHOD_OPTIONS(t));
const hasComments = computed(() => HAS_COMMENT_OPTIONS(t));

const getFilterSource = type => {
  const sources = {
    agents: agents.value,
    inboxes: inboxes.value,
    teams: teams.value,
    ratings: ratings.value,
    labels: labels.value,
    exclude_labels: labels.value,
    resolution_methods: resolutionMethods.value,
    has_comments: hasComments.value,
  };
  return sources[type] || [];
};

const getAvailableLabelOptions = () => {
  const selectedIds = appliedFilters.value.label_title;
  const excludedIds = appliedFilters.value.exclude_label_title;
  return buildLabelsList(labels.value).filter(
    l => !selectedIds.includes(l.id) && !excludedIds.includes(l.id)
  );
};

const getFilterOptions = type => {
  if (type === 'ratings') {
    const selectedIds = appliedFilters.value.rating;
    return ratings.value.filter(r => !selectedIds.includes(r.id));
  }

  if (type === 'resolution_methods') {
    const selectedIds = appliedFilters.value.resolution_method;
    return resolutionMethods.value.filter(r => !selectedIds.includes(r.id));
  }

  if (type === 'has_comments') {
    const selectedIds = appliedFilters.value.has_comment;
    return hasComments.value.filter(r => !selectedIds.includes(r.id));
  }

  if (type === 'labels') {
    return getAvailableLabelOptions();
  }

  const options = buildFilterList(getFilterSource(type), type);
  const filterKey = getFilterType(type, 'typeToKey');
  const selectedIds = appliedFilters.value[filterKey];

  if (Array.isArray(selectedIds) && selectedIds.length > 0) {
    return options.filter(option => !selectedIds.includes(option.id));
  }

  return options;
};

const filterListMenuItems = computed(() => {
  const filterTypes = [
    {
      id: '1',
      name: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'),
      type: 'agents',
    },
    {
      id: '2',
      name: t('CSAT_REPORTS.FILTERS.INBOXES.LABEL'),
      type: 'inboxes',
    },
    {
      id: '3',
      name: t('CSAT_REPORTS.FILTERS.RATINGS.LABEL'),
      type: 'ratings',
    },
    {
      id: '7',
      name: t('CSAT_REPORTS.FILTERS.RESOLVED_BY.LABEL'),
      type: 'resolution_methods',
    },
    {
      id: '8',
      name: t('CSAT_REPORTS.FILTERS.HAS_COMMENT.LABEL'),
      type: 'has_comments',
    },
    {
      id: '5',
      name: t('CSAT_REPORTS.FILTERS.LABELS.LABEL'),
      type: 'labels',
      options: getAvailableLabelOptions(),
    },
  ];

  if (props.showTeamFilter) {
    filterTypes.splice(2, 0, {
      id: '4',
      name: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'),
      type: 'teams',
    });
  }

  return filterTypes.map(({ id, name, type, options }) => ({
    id,
    name,
    type,
    options: options || getFilterOptions(type),
  }));
});

const activeFilters = computed(() => {
  const chips = [];

  const arrayKeys = [
    'user_ids',
    'inbox_id',
    'team_id',
    'rating',
    'resolution_method',
    'has_comment',
  ];

  arrayKeys.forEach(key => {
    const ids = appliedFilters.value[key];
    if (!Array.isArray(ids) || ids.length === 0) return;

    const filterType = getFilterType(key, 'keyToType');
    const items = getFilterSource(filterType);

    ids.forEach(id => {
      let name;
      if (key === 'rating' && id === 'no_response') {
        name = t('CSAT_REPORTS.FILTERS.RATINGS.NO_RESPONSE');
      } else {
        const item = items.find(
          i => i.id === id || i.id?.toString() === id?.toString()
        );
        name = item?.name || `ID: ${id}`;
      }

      chips.push({
        id,
        name,
        type: filterType,
        filterKey: key,
        isExcluded: false,
        isLabel: false,
        options: getFilterOptions(filterType),
      });
    });
  });

  labelChipOrder.value.forEach(id => {
    const isExcluded = appliedFilters.value.exclude_label_title.includes(id);
    const isIncluded = appliedFilters.value.label_title.includes(id);
    if (!isExcluded && !isIncluded) return;

    chips.push({
      id,
      name: id,
      type: isExcluded ? 'exclude_labels' : 'labels',
      filterKey: isExcluded ? 'exclude_label_title' : 'label_title',
      isExcluded,
      isLabel: true,
      options: getFilterOptions('labels'),
    });
  });

  return chips;
});

const hasActiveFilters = computed(() =>
  Object.values(appliedFilters.value).some(
    value => Array.isArray(value) && value.length > 0
  )
);

const hasLabelFilters = computed(() =>
  activeFilters.value.some(filter => filter.isLabel)
);

const updateURLParams = () => {
  const comparisonPayload = comparisonPeriodsPayload();
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    comparisonPeriods: comparisonPayload,
    filters: {
      agent_id: appliedFilters.value.user_ids,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      rating: appliedFilters.value.rating,
      label_title: appliedFilters.value.label_title,
      exclude_label_title: appliedFilters.value.exclude_label_title,
    },
  });
  if (!comparisonPayload.length) delete params.comparison_periods;
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    comparisonPeriods: comparisonPeriodsPayload(),
    selectedAgents: appliedFilters.value.user_ids.map(id => ({ id })),
    selectedInboxes: appliedFilters.value.inbox_id.map(id => ({ id })),
    selectedTeams: appliedFilters.value.team_id.map(id => ({ id })),
    selectedRatings: appliedFilters.value.rating.map(value => ({ value })),
    selectedLabels: appliedFilters.value.label_title.map(value => ({ value })),
    excludedLabels: appliedFilters.value.exclude_label_title.map(value => ({
      value,
    })),
    resolutionMethod: appliedFilters.value.resolution_method[0] || 'all',
    hasComment: appliedFilters.value.has_comment[0] || 'all',
  });
};

const closeDropdown = () => {
  showDropdownMenu.value = false;
};

const showDropdown = () => {
  showDropdownMenu.value = !showDropdownMenu.value;
};

const SINGLE_VALUE_FILTER_TYPES = new Set([
  'resolution_methods',
  'has_comments',
]);

const addFilter = item => {
  const { type, id } = item;
  const filterKey = getFilterType(type, 'typeToKey');

  if (SINGLE_VALUE_FILTER_TYPES.has(type)) {
    appliedFilters.value[filterKey] = [id];
    closeDropdown();
    return;
  }

  if (!appliedFilters.value[filterKey].includes(id)) {
    appliedFilters.value[filterKey].push(id);
  }

  if (type === 'labels' && !labelChipOrder.value.includes(id)) {
    labelChipOrder.value.push(id);
  }

  // Keep menu open for multi-select filters
};

const removeFilter = (filterKey, id) => {
  const index = appliedFilters.value[filterKey].indexOf(id);
  if (index !== -1) appliedFilters.value[filterKey].splice(index, 1);

  if (filterKey === 'label_title' || filterKey === 'exclude_label_title') {
    labelChipOrder.value = labelChipOrder.value.filter(
      labelId => labelId !== id
    );
  }
};

const toggleLabelFilterMode = filter => {
  if (!filter.isLabel) return;

  const fromKey = filter.filterKey;
  const toKey =
    fromKey === 'label_title' ? 'exclude_label_title' : 'label_title';

  const fromIndex = appliedFilters.value[fromKey].indexOf(filter.id);
  if (fromIndex !== -1) appliedFilters.value[fromKey].splice(fromIndex, 1);
  if (!appliedFilters.value[toKey].includes(filter.id)) {
    appliedFilters.value[toKey].push(filter.id);
  }
};

const clearAllFilters = () => {
  appliedFilters.value = {
    user_ids: [],
    inbox_id: [],
    team_id: [],
    rating: [],
    label_title: [],
    exclude_label_title: [],
    resolution_method: [],
    has_comment: [],
  };
  labelChipOrder.value = [];
  closeDropdown();
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

const addComparisonPeriod = () => {
  if (comparisonRanges.value.length >= MAX_COMPARISON_PERIODS) return;
  const [pStart, pEnd] = customDateRange.value;
  const days = Math.max(differenceInCalendarDays(pEnd, pStart), 1);
  const end = subDays(pStart, 1);
  const start = subDays(end, days);
  comparisonRanges.value = [
    ...comparisonRanges.value,
    {
      dates: [start, end],
      rangeType: DATE_RANGE_TYPES.CUSTOM_RANGE,
    },
  ];
};

const removeComparisonPeriod = index => {
  comparisonRanges.value = comparisonRanges.value.filter((_, i) => i !== index);
};

const onComparisonDatesChanged = (index, value) => {
  const [startDate, endDate, rangeType] = value;
  const row = comparisonRanges.value[index];
  if (!row) return;
  row.dates = [startDate, endDate];
  row.rangeType = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
};

onMounted(() => {
  emitChange();
});
</script>

<template>
  <div class="flex flex-col flex-wrap w-full gap-3">
    <div class="flex min-w-0 flex-wrap items-center gap-2">
      <div class="min-w-0 flex-1 md:min-w-[12rem]">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          mark-incomplete-current-day
          @date-range-changed="onDateRangeChange"
        />
      </div>
      <span class="shrink-0 text-xs text-n-slate-11">
        {{ $t('REPORT.UTC_LABEL') }}
      </span>
      <div
        v-for="(cr, idx) in comparisonRanges"
        :key="idx"
        class="flex min-w-0 flex-1 items-center gap-1 md:min-w-[12rem]"
      >
        <div class="min-w-0 flex-1">
          <WootDatePicker
            v-model:date-range="cr.dates"
            v-model:range-type="cr.rangeType"
            panel-align="end"
            @date-range-changed="v => onComparisonDatesChanged(idx, v)"
          />
        </div>
        <V4Button
          v-tooltip.top="$t('REPORT.COMPARISON.REMOVE')"
          size="xs"
          variant="faded"
          color="slate"
          icon="i-ph-x"
          class="shrink-0"
          @click="removeComparisonPeriod(idx)"
        />
      </div>
      <V4Button
        v-if="comparisonRanges.length < MAX_COMPARISON_PERIODS"
        :label="$t('REPORT.COMPARISON.ADD')"
        size="xs"
        variant="faded"
        color="teal"
        icon="i-ph-plus"
        class="self-start"
        @click="addComparisonPeriod"
      />
    </div>

    <div class="flex w-full flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <AddFilterChip
          placeholder-i18n-key="CSAT_REPORTS.FILTERS.INPUT_PLACEHOLDER"
          :name="$t('CSAT_REPORTS.FILTERS.ADD_FILTER')"
          :menu-option="filterListMenuItems"
          :show-menu="showDropdownMenu"
          :empty-state-message="$t('CSAT_REPORTS.FILTERS.NO_FILTER')"
          enable-search
          @toggle-dropdown="showDropdown"
          @close-dropdown="closeDropdown"
          @add-filter="addFilter"
        />

        <FilterButton
          v-if="hasActiveFilters"
          :button-text="$t('CSAT_REPORTS.FILTERS.CLEAR_ALL')"
          @click="clearAllFilters"
        />

        <V4Button
          :label="$t('REPORT.FILTER_ACTIONS.APPLY')"
          size="sm"
          @click="emitChange"
        />
      </div>

      <div v-if="hasActiveFilters" class="flex flex-col gap-1.5">
        <div class="flex flex-wrap gap-2">
          <div
            v-for="filter in activeFilters"
            :key="
              filter.isLabel
                ? `label-${filter.id}`
                : `${filter.filterKey}-${filter.id}`
            "
            class="inline-flex items-center gap-1 rounded-full text-sm font-medium transition-colors"
            :class="[
              filter.isExcluded
                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                : 'bg-n-25 dark:bg-n-700 text-n-900 dark:text-n-25',
            ]"
          >
            <button
              type="button"
              class="inline-flex items-center gap-1.5 pl-3 py-1.5 rounded-l-full"
              :class="
                filter.isExcluded
                  ? 'hover:bg-red-100 dark:hover:bg-red-900/50'
                  : 'hover:bg-n-50 dark:hover:bg-n-600'
              "
              :title="
                filter.isLabel
                  ? filter.isExcluded
                    ? $t('CSAT_REPORTS.FILTERS.LABELS.TOGGLE_TO_INCLUDE')
                    : $t('CSAT_REPORTS.FILTERS.LABELS.TOGGLE_TO_EXCLUDE')
                  : undefined
              "
              @click="
                filter.isLabel
                  ? toggleLabelFilterMode(filter)
                  : removeFilter(filter.filterKey, filter.id)
              "
            >
              <span
                v-if="filter.isExcluded"
                class="text-xs font-semibold opacity-80"
              >
                {{ $t('CSAT_REPORTS.FILTERS.LABELS.EXCLUDE_PREFIX') }}
              </span>
              <span>{{ filter.name }}</span>
            </button>
            <button
              type="button"
              class="inline-flex items-center pr-2.5 py-1.5 rounded-r-full"
              :class="
                filter.isExcluded
                  ? 'hover:bg-red-100 dark:hover:bg-red-900/50'
                  : 'hover:bg-n-50 dark:hover:bg-n-600'
              "
              :aria-label="$t('CSAT_REPORTS.FILTERS.CLEAR_ALL')"
              @click="removeFilter(filter.filterKey, filter.id)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
        <p v-if="hasLabelFilters" class="text-xs text-n-slate-11">
          {{ $t('CSAT_REPORTS.FILTERS.LABELS.CLICK_HINT') }}
        </p>
      </div>
    </div>
  </div>
</template>
